import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type AccountDetail,
  type AccountListItem,
  type AccountSummary,
  type BalanceDto,
  type CreateAccountInput,
  type CreateBalanceInput,
  type ListAccountsFilters,
  type UpdateAccountInput,
  accountsApi,
} from "../lib/accounts";

const baseKey = ["accounts"] as const;

export function useAccounts(filters: ListAccountsFilters = {}) {
  return useQuery<{ accounts: AccountListItem[] }>({
    queryKey: [...baseKey, "list", filters],
    queryFn: () => accountsApi.list(filters),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useAccountSummary() {
  return useQuery<AccountSummary>({
    queryKey: [...baseKey, "summary"],
    queryFn: () => accountsApi.summary(),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useAccount(id: string) {
  return useQuery<AccountDetail>({
    queryKey: [...baseKey, "detail", id],
    queryFn: () => accountsApi.get(id),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(id),
  });
}

export function useListBalances(accountId: string, limit = 50, offset = 0) {
  return useQuery<{ balances: BalanceDto[] }>({
    queryKey: [...baseKey, "balances", accountId, limit, offset],
    queryFn: () => accountsApi.listBalances(accountId, limit, offset),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(accountId),
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  return qc.invalidateQueries({ queryKey: baseKey });
}

export function useCreateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAccountInput) => accountsApi.create(input),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateAccount(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateAccountInput) => accountsApi.update(id, input),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useSoftDeleteAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => accountsApi.softDelete(id),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useHardDeleteAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => accountsApi.hardDelete(id),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useAddBalance(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBalanceInput) => accountsApi.addBalance(accountId, input),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteBalance(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (balanceId: string) => accountsApi.deleteBalance(accountId, balanceId),
    onSuccess: () => invalidateAll(qc),
  });
}
